import { Period } from '../../../types/fiscalYear/FiscalYear'
import { AllocationFunction, MonthlyBudget, AllocationOptions, HistoryDataPoint, AllocationMethod } from '../types'
import { BalanceRegistry } from '../../financialStatements/types'
import { createDataKey } from '../../../shared/utils/balanceRegistryUtils'

/**
 * Pyöristää luvun annettuun desimaalimäärään
 *
 * HUOM: Allokaatiofunktiot eivät enää käytä pyöristystä tallennusvaiheessa.
 * Arvot tallennetaan täydellä floating point -tarkkuudella ja pyöristys
 * tapahtuu vain UI:n formatoinnissa.
 *
 * Tämä funktio exportataan muita käyttötarkoituksia varten.
 *
 * @param num - Pyöristettävä luku
 * @param decimals - Desimaalien määrä (oletus: 2)
 */
export const roundToDecimals = (num: number, decimals = 2): number => {
  const factor = 10 ** decimals
  return Math.round(num * factor) / factor
}

/**
 * Luo period-avaimen vertailua varten
 */
export const getPeriodKey = (period: Period): string => {
  return `${period.year}-${String(period.month).padStart(2, '0')}`
}

/**
 * Generoi Period[] arrayn annetun aikavälin perusteella
 *
 * @param startPeriod - Alkuperiodi
 * @param endPeriod - Loppuperiodi
 * @returns Period[] array aikavälillä (sisältäen molemmat päät)
 */
export const generatePeriodRange = (startPeriod: Period, endPeriod: Period): Period[] => {
  const periods: Period[] = []

  let currentYear = startPeriod.year
  let currentMonth = startPeriod.month

  const endTimestamp = createDataKey(endPeriod.year, endPeriod.month)

  while (createDataKey(currentYear, currentMonth) <= endTimestamp) {
    periods.push({
      period: 0,
      year: currentYear,
      month: currentMonth
    })

    // Move to next month
    currentMonth += 1
    if (currentMonth > 12) {
      currentMonth = 1
      currentYear += 1
    }
  }

  return periods
}

/**
 * Rakentaa HistoryDataPoint[] arrayn annetun aikavälin ja actuals datan perusteella
 *
 * @param historyRange - Historiajakso (startPeriod, endPeriod)
 * @param actuals - Actuals BalanceRegistry josta haetaan arvot
 * @param budgets - Valinnainen budgets BalanceRegistry (jos halutaan palauttaa myös budget arvot)
 * @returns HistoryDataPoint[] array
 */
export const buildHistoryData = (
  historyRange: { startPeriod: Period; endPeriod: Period },
  actuals?: BalanceRegistry,
  budgets?: BalanceRegistry
): HistoryDataPoint[] => {
  const periods = generatePeriodRange(historyRange.startPeriod, historyRange.endPeriod)

  return periods.map(period => {
    const timestamp = createDataKey(period.year, period.month)

    return {
      period,
      actuals: actuals?.elements?.[timestamp] ?? null,
      budget: budgets?.elements?.[timestamp] ?? null
    }
  })
}

/**
 * Validoi että historyRange:n pituus vastaa targetPeriods:in pituutta
 *
 * @param historyRange - Historiajakso
 * @param targetPeriods - Kohdeperiodit
 * @returns true jos pituudet täsmäävät, false jos eivät
 */
export const validateHistoryRangeLength = (
  historyRange: { startPeriod: Period; endPeriod: Period },
  targetPeriods: Period[]
): boolean => {
  const historyPeriods = generatePeriodRange(historyRange.startPeriod, historyRange.endPeriod)
  return historyPeriods.length === targetPeriods.length
}

/**
 * DIVIDE_EVENLY: Jakaa summan tasan kaikille kuukausille + valinnainen kasvuprosentti
 *
 * Esimerkki: 12 000 € / 12 kk = 1 000 €/kk
 * Kasvu 5%: 12 000 € × 1.05 / 12 kk = 1 050 €/kk
 *
 * Floating point -ero korjataan viimeiseen kuukauteen, jotta summa täsmää.
 * Arvot tallennetaan täydellä tarkkuudella; pyöristys tapahtuu vain UI:ssa.
 */
export const divideEvenly: AllocationFunction = (
  totalSum: number,
  targetPeriods: Period[],
  options?: AllocationOptions
): MonthlyBudget[] => {
  if (targetPeriods.length === 0) {
    return []
  }

  const growthFactor = 1 + (options?.growthPercentage ?? 0)
  const adjustedTotal = totalSum * growthFactor
  const monthlyAmount = adjustedTotal / targetPeriods.length

  const result: MonthlyBudget[] = targetPeriods.map(period => ({
    year: period.year,
    month: period.month,
    value: monthlyAmount
  }))

  // Korjaa floating point -ero viimeiseen kuukauteen, jotta summa täsmää tarkasti
  if (result.length > 0) {
    const calculatedTotal = monthlyAmount * targetPeriods.length
    const floatingPointError = adjustedTotal - calculatedTotal

    if (floatingPointError !== 0) {
      const lastIndex = result.length - 1
      result[lastIndex].value = (result[lastIndex].value ?? 0) + floatingPointError
    }
  }

  return result
}

/**
 * ADD_TO_EVERY_MONTH: Lisää saman summan jokaiseen kuukauteen + valinnainen kasvuprosentti
 *
 * Esimerkki: 12 000 € → jokainen kuukausi saa 12 000 €
 * Kasvu 5%: 12 000 € × 1.05 = 12 600 € jokaiseen kuukauteen
 * Kokonaissumma = 12 600 € × 12 kk = 151 200 €
 *
 * Arvot tallennetaan täydellä tarkkuudella; pyöristys tapahtuu vain UI:ssa.
 */
export const addToEveryMonth: AllocationFunction = (
  totalSum: number,
  targetPeriods: Period[],
  options?: AllocationOptions
): MonthlyBudget[] => {
  const growthFactor = 1 + (options?.growthPercentage ?? 0)
  const adjustedAmount = totalSum * growthFactor

  return targetPeriods.map(period => ({
    year: period.year,
    month: period.month,
    value: adjustedAmount
  }))
}

/**
 * WEIGHTED: Jakaa summan historiapainotuksella + valinnainen kasvuprosentti
 *
 * Esimerkki:
 * Historia 01-06/2024 actuals: [1000, 1500, 2000, 1500, 1000, 1000] = 8000€
 * Painot: [0.125, 0.1875, 0.25, 0.1875, 0.125, 0.125]
 * Summa 12000€, kasvu 5%:
 *   01: 12000 × 0.125 × 1.05 = 1575€
 *   02: 12000 × 0.1875 × 1.05 = 2362.50€
 *   ...
 *
 * Fallback: Jos historiasumma = 0 → DIVIDE_EVENLY
 */
export const weightedAllocation: AllocationFunction = (
  totalSum: number,
  targetPeriods: Period[],
  options?: AllocationOptions
): MonthlyBudget[] => {
  if (targetPeriods.length === 0) {
    return []
  }

  // Validate required options
  if (!options?.historyRange) {
    console.warn('WEIGHTED requires historyRange option, falling back to DIVIDE_EVENLY')
    return divideEvenly(totalSum, targetPeriods, options)
  }

  if (!options?.historyData) {
    console.warn('WEIGHTED requires historyData option, falling back to DIVIDE_EVENLY')
    return divideEvenly(totalSum, targetPeriods, options)
  }

  const { historyData } = options
  const growthFactor = 1 + (options.growthPercentage ?? 0)

  // Validate that history data length matches target periods length
  if (historyData.length !== targetPeriods.length) {
    console.warn(
      `History data length (${historyData.length}) does not match target periods length (${targetPeriods.length}), falling back to DIVIDE_EVENLY`
    )
    return divideEvenly(totalSum, targetPeriods, options)
  }

  // Extract actuals from history data (use 0 if null)
  const actuals = historyData.map(h => h.actuals ?? 0)

  // Calculate total actuals
  const totalActuals = actuals.reduce((sum, val) => sum + val, 0)

  // Fallback to even distribution if no historical data
  if (totalActuals === 0) {
    console.warn('Total historical actuals is zero, falling back to DIVIDE_EVENLY')
    return divideEvenly(totalSum, targetPeriods, options)
  }

  // Calculate weights for each period
  const weights = actuals.map(actual => actual / totalActuals)

  // Allocate budget based on weights and growth factor
  // Arvot tallennetaan täydellä tarkkuudella; pyöristys tapahtuu vain UI:ssa
  const result: MonthlyBudget[] = targetPeriods.map((period, index) => ({
    year: period.year,
    month: period.month,
    value: totalSum * weights[index] * growthFactor
  }))

  // Korjaa floating point -ero viimeiseen kuukauteen, jotta summa täsmää tarkasti
  if (result.length > 0) {
    const calculatedTotal = result.reduce((sum, mb) => sum + (mb.value ?? 0), 0)
    const expectedTotal = totalSum * growthFactor
    const floatingPointError = expectedTotal - calculatedTotal

    if (floatingPointError !== 0) {
      const lastIndex = result.length - 1
      result[lastIndex].value = (result[lastIndex].value ?? 0) + floatingPointError
    }
  }

  return result
}

/**
 * Pääfunktio joka valitsee oikean jakotavan
 */
export const allocateBudget = (
  method: AllocationMethod,
  totalSum: number,
  targetPeriods: Period[],
  options?: AllocationOptions
): MonthlyBudget[] => {
  switch (method) {
    case AllocationMethod.DIVIDE_EVENLY:
      return divideEvenly(totalSum, targetPeriods, options)
    case AllocationMethod.ADD_TO_EVERY_MONTH:
      return addToEveryMonth(totalSum, targetPeriods, options)
    case AllocationMethod.WEIGHTED:
      return weightedAllocation(totalSum, targetPeriods, options)
    default:
      throw new Error(`Unknown allocation method: ${method}`)
  }
}
